import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Calendar as CalendarIcon, Video, ExternalLink, Clock, RefreshCw, CalendarPlus } from 'lucide-react';
import { startMeeting } from '../../redux/meetingSlice';
import { refundPayment, clearRefundResult } from '../../redux/paymentSlice';
import { reopenAvailability } from '../../redux/availabilitySlice';
import { toast } from 'react-toastify';
import { formatDateRangeForZone, formatDateTimeForZone, parseBackendDate, USER_TIMEZONE } from '../../utils/dateUtils';

const truncateLink = (link) => {
  if (!link) return "";
  const cleaned = link.replace(/^https?:\/\//i, "");
  return cleaned.length > 50 ? `${cleaned.slice(0, 47)}...` : cleaned;
};

const DetailPanel = ({
  selectedDate,
  currentMonth,
  currentYear,
  userRequests,
  currentUser
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [startingMeetingId, setStartingMeetingId] = useState(null);
  const [refundingAvailabilityId, setRefundingAvailabilityId] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [refundReason, setRefundReason] = useState("");
  

  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedReschedule, setSelectedReschedule] = useState(null);

  
  const TIME_ZONE = USER_TIMEZONE;
  
  const formatTherapistName = (therapist) => {
    const fallback = t("unknownTherapist");
    if (!therapist) return fallback;
    const parts = [];
    if (therapist.name) parts.push(therapist.name);
    if (therapist.surname) parts.push(therapist.surname);
    return parts.length ? parts.join(" ") : fallback;
  };
  

  const { refundLoading, refundResult, refundError } = useSelector(state => state.payment);


  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000); // Her 30 saniyede güncelle (daha sık kontrol)
    
    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    if (refundResult) {
      const availabilityId = selectedRefund?.availabilityId;
      toast.success(t("refundSuccessful"));
      setShowRefundModal(false);
      setSelectedRefund(null);
      setRefundReason("");
      dispatch(clearRefundResult());

      const doReopen = availabilityId
        ? dispatch(reopenAvailability(availabilityId))
        : Promise.resolve();

      doReopen.finally(() => window.location.reload());
    }

    if (refundError) {
      toast.error(refundError.message || t("refundFailed"));
      dispatch(clearRefundResult());
    }
  }, [refundResult, refundError, dispatch, t, selectedRefund?.availabilityId]);


  const canJoinMeeting = (startDate) => {
    if (!startDate) return false;
    
    const meetingTime = new Date(startDate);
    const now = currentTime;
    
    // Toplantı zamanı ile şu anki zaman arasındaki fark (dakika olarak)
    const timeDiffMs = meetingTime.getTime() - now.getTime();
    const minutesRemaining = Math.floor(timeDiffMs / (1000 * 60));
    
    // 5 dakika öncesinden başlatılabilir, 60 dakika geç start'a izin ver
    return minutesRemaining <= 5 && minutesRemaining >= -60;
  };


  const isRefunded = (request) => {
    // payment objesini kontrol et
    if (request.payment && typeof request.payment === 'object') {
      // Doğrudan payment.status kontrol et
      if (request.payment.status === 'refunded') {
        return true;
      }
      
      // payment.details.refund kontrol et
      if (request.payment.details && 
          request.payment.details.refund && 
          request.payment.details.refund.status === 'succeeded') {
        return true;
      }
    }
    
    return false;
  };


  const canRefund = (startDate, requestStatus, request) => {
    if (!startDate) return false;
    
    // Eğer refund edildiyse artık refund edilemez
    if (isRefunded(request)) {
      return false;
    }
    
    // Sadece 'accepted' veya 'completed' statüsündeki randevular iade edilebilir
    if (requestStatus !== 'accepted' && requestStatus !== 'completed') {
      return false;
    }
    
    const meetingTime = new Date(startDate);
    const now = currentTime;
    
    // Toplantı zamanına kalan süre (saat olarak)
    const timeDiffMs = meetingTime.getTime() - now.getTime();
    const hoursRemaining = timeDiffMs / (1000 * 60 * 60);
    
    // 24 saatten fazla varsa iade edilebilir
    return hoursRemaining > 24;
  };


  const getRefundTimeRemaining = (startDate) => {
    if (!startDate) return null;
    
    const meetingTime = new Date(startDate);
    const now = currentTime;
    const timeDiffMs = meetingTime.getTime() - now.getTime();
    const hoursRemaining = timeDiffMs / (1000 * 60 * 60);
    
    if (hoursRemaining > 24) {
      const days = Math.floor(hoursRemaining / 24);
      const hours = Math.floor(hoursRemaining % 24);
      if (days > 0) {
        return `${days} ${t("days")} ${hours} ${t("hours")}`;
      }
      return `${Math.floor(hoursRemaining)} ${t("hours")}`;
    } else if (hoursRemaining > 0) {
      return `${Math.floor(hoursRemaining)} ${t("hours")} (${t("refundNotAvailable")})`;
    } else {
      return t("refundExpired");
    }
  };


  const getTimeRemaining = (startDate) => {
    if (!startDate) return null;
    
    const meetingTime = new Date(startDate);
    const now = currentTime;
    const timeDiffMs = meetingTime.getTime() - now.getTime();
    const minutesRemaining = Math.floor(timeDiffMs / (1000 * 60));
    const minuteLabel = t("minuteAbbr");
    const hourLabel = t("hourAbbr");
    
    if (minutesRemaining > 5) {
      const hours = Math.floor(minutesRemaining / 60);
      const mins = minutesRemaining % 60;
      if (hours > 0) {
        return `${hours}${hourLabel} ${mins}${minuteLabel}`;
      }
      return `${mins}${minuteLabel}`;
    } else if (minutesRemaining > 0) {
      return `${minutesRemaining}${minuteLabel}`;
    } else if (minutesRemaining >= -60) {
      return t("meetingActive");
    } else {
      return t("meetingEnded");
    }
  };


  const handleOpenRefundModal = (request, availabilityId) => {
    setSelectedRefund({ request, availabilityId });
    setShowRefundModal(true);
    setRefundReason("");
  };


  const handleCloseRefundModal = () => {
    setShowRefundModal(false);
    setSelectedRefund(null);
    setRefundReason("");
  };


  const handleConfirmRefund = async () => {
    if (!selectedRefund) return;
    
    const { availabilityId } = selectedRefund;
    
    if (!refundReason.trim()) {
      toast.warning(t("pleaseEnterRefundReason"));
      return;
    }
    
    setRefundingAvailabilityId(availabilityId);
    
    try {
      await dispatch(refundPayment({ 
        availabilityId, 
        reason: refundReason.trim() 
      }));
    } catch (error) {
      console.error('Refund error:', error);
    } finally {
      setRefundingAvailabilityId(null);
    }
  };


  const handleOpenRescheduleModal = (request, availabilityId) => {
    setSelectedReschedule({ request, availabilityId });
    setShowRescheduleModal(true);
  };


  const handleCloseRescheduleModal = () => {
    setShowRescheduleModal(false);
    setSelectedReschedule(null);
  };


  const handleReschedule = () => {
    if (!selectedReschedule) return;
    
    const therapistId = selectedReschedule.request?.therapistId?._id || 
                        selectedReschedule.request?.therapistId;
    
    if (therapistId) {
      // Terapist profiline yönlendir - orada yeni zaman seçebilir
      toast.info(t("selectNewTimeFromTherapistProfile"));
      // Session storage'a mevcut availabilityId'yi kaydet
      sessionStorage.setItem('rescheduleFromAvailabilityId', selectedReschedule.availabilityId);
      window.location.href = `/therapistInfo/${therapistId}`;
    } else {
      toast.error(t("therapistNotFound"));
    }
    
    handleCloseRescheduleModal();
  };


  const handleJoinMeeting = (meetingUrl, meetingId) => {
    if (!meetingUrl) {
      toast.error(t("meetingUrlNotFound"));
      return;
    }

    try {
      // Yeni sekmede meeting URL'ini aç
      window.open(meetingUrl, '_blank', 'noopener,noreferrer');
      toast.success(t("meetingOpened"));
    } catch (error) {
      toast.error(t("errorOpeningMeeting"));
      console.error('Error opening meeting URL:', error);
    }
  };


  const handleStartMeeting = async (meetingId, meetingUrl) => {
    if (!meetingId) {
      toast.error(t("meetingIdNotFound"));
      return;
    }

    setStartingMeetingId(meetingId);

    try {
      // Redux action ile API'ye istek at
      const result = await dispatch(startMeeting(meetingId));
      
      if (startMeeting.fulfilled.match(result)) {
        // Başarılı olursa URL'i aç
        const urlToOpen = result.payload.meetingUrl || meetingUrl;
        if (urlToOpen) {
          window.open(urlToOpen, '_blank', 'noopener,noreferrer');
          toast.success(t("meetingStarted"));
        } else {
          toast.error(t("meetingUrlNotFound"));
        }
      } else {
        toast.error(result.payload || t("errorStartingMeeting"));
      }
    } catch (error) {
      toast.error(error.message || t("errorStartingMeeting"));
    } finally {
      setStartingMeetingId(null);
    }
  };


  const selectedDateRequests = userRequests.filter(request => 
    request.date === selectedDate && 
    request.month === currentMonth && 
    request.year === currentYear
  );


  const today = new Date();


  const futureRequests = userRequests.filter(request => {
    const requestDate = new Date(request.year, request.month, request.date);
    return requestDate >= today;
  });


  const pastRequests = userRequests.filter(request => {
    const requestDate = new Date(request.year, request.month, request.date);
    return requestDate < today;
  });

  const formatLabel = (key) => {
    const translation = t(key);
    if (translation && translation !== key) return translation;

    return key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };


  const getStatusText = (status, request) => {
    // Önce refund durumunu kontrol et
    if (isRefunded(request)) {
      return formatLabel("refunded");
    }
    
    switch(status) {
      case 'pending':
        return formatLabel("pending");
      case 'accepted':
        return formatLabel("accepted");
      case 'rejected':
        return formatLabel("rejected");
      case 'canceled':
        return formatLabel("canceled");
      case 'completed':
        return formatLabel("completed");
      default:
        return status ? formatLabel(status.toLowerCase()) : "";
    }
  };


  const getStatusColor = (status, request) => {
    // Önce refund durumunu kontrol et
    if (isRefunded(request)) {
      return "bg-purple-100 border-purple-300 text-purple-800";
    }
    
    switch(status) {
      case 'pending':
        return "bg-yellow-100 border-yellow-300 text-yellow-800";
      case 'accepted':
        return "bg-green-100 border-green-300 text-green-800";
      case 'rejected':
        return "bg-red-100 border-red-300 text-red-800";
      case 'canceled':
        return "bg-gray-100 border-gray-300 text-gray-800";
      case 'completed':
        return "bg-blue-100 border-blue-300 text-blue-800";
      default:
        return "bg-gray-100 border-gray-300 text-gray-800";
    }
  };

  const localizeMeetingName = (name) => {
    if (!name) return "";

    if (!i18n.language || !i18n.language.toLowerCase().startsWith("en")) {
      return name;
    }

    const normalized = name.trim();
    const replacements = [
      {
        match: /terapi seans[ıi] rezervasyonu/gi,
        getValue: () => t("therapySessionReservation")
      },
      {
        match: /terapi seansı/gi,
        getValue: () => t("therapySession"),
      },
    ];

    let output = normalized;

    replacements.forEach(({ match, getValue }) => {
      const replacement = getValue();
      output = output.replace(match, replacement);
    });

    return output;
  };

  const formatDateToLocal = (date) => {
    if (!date) return "";

    try {
      return formatDateTimeForZone(date, TIME_ZONE);
    } catch (error) {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");

      return `${day}.${month}.${year} ${hours}:${minutes}`;
    }
  };

  return (
    <div className="w-[277px] h-auto mt-4 md:mt-16 md:mr-12 flex flex-col order-2 md:order-2">
      <aside>
        {/* Başlık */}
        <h3 className="text-[20px] font-bold font-poppins mb-2">
          {selectedDate
            ? `${t(
                `${new Date(currentYear, currentMonth).toLocaleString(
                  "en-US",
                  { month: "long" }
                )}`
              )} ${selectedDate}, ${t(
                `${new Date(
                  currentYear,
                  currentMonth,
                  selectedDate
                ).toLocaleString("en-US", {
                  weekday: "long",
                })}`
              )}`
            : t("noDateSelected")}
        </h3>
        <hr className="w-[277px] border-[3px] rounded-2xl mb-3 border-cards-border" />

        {/* Takvim Mesajı */}
        <div className="flex flex-row items-center gap-2 mb-4">
          <CalendarIcon className="w-6 h-6 text-[#024202]" />
          <p className="text-[16px] font-bold font-poppins text-[#454545]">
            {selectedDateRequests.length > 0 ? (
              t("selectedDateHasRequests", { count: selectedDateRequests.length })
            ) : (
              t("selectedDateNoRequests")
            )}
          </p>
        </div>

        {/* Seçili gün için request detayları */}
        {selectedDateRequests.length > 0 && (
          <div className="mb-6">
            <div className="flex flex-row items-center gap-2 mb-2">
              <CalendarIcon className="w-5 h-5 text-text" />
              <h4 className="text-[18px] font-bold font-poppins text-text">
                {formatLabel("requestDetails")}
              </h4>
            </div>
            
            {/* Scrollable Request Details Container */}
            <div className="max-h-96 overflow-y-auto space-y-3 pr-2">
              {selectedDateRequests.map((request) => {
                // Başlangıç ve bitiş tarihlerini al
                const slotInfo = request.availabilityIds && request.availabilityIds.length > 0
                  ? request.availabilityIds[0]
                  : null;

                const availabilityId = slotInfo?._id || null;

                const startDate = request.appointmentDate
                  ? new Date(request.appointmentDate)
                  : slotInfo
                    ? parseBackendDate(slotInfo.startDate)
                    : null;
                const endDate = slotInfo ? parseBackendDate(slotInfo.endDate) : null;

                const formattedDate = request.formattedDate && request.formattedTime
                  ? `${request.formattedDate} ${request.formattedTime}`
                  : formatDateToLocal(startDate);
                const formattedDateWithRange = request.formattedDate && request.formattedTimeRange
                  ? `${request.formattedDate} ${request.formattedTimeRange}`
                  : formatDateRangeForZone(startDate, endDate, TIME_ZONE);
                  
                // Meeting bilgilerini al
                const meetingId = request.meetingId && typeof request.meetingId === 'object' 
                  ? request.meetingId._id 
                  : null;
                  
                const meetingName = request.meetingId && typeof request.meetingId === 'object' 
                  ? request.meetingId.name 
                  : "";
                const localizedMeetingName = localizeMeetingName(meetingName);
                  
                const meetingUrl = request.meetingId && typeof request.meetingId === 'object' 
                  ? request.meetingId.meetingUrl 
                  : null;
                  
                // Toplantıya katılabilir mi kontrol et
                const canJoin = canJoinMeeting(startDate) && meetingUrl;
                const isStarting = startingMeetingId === meetingId;
                const timeRemaining = getTimeRemaining(startDate);
                
                // Refund kontrolü
                const isRequestRefunded = isRefunded(request);
                const isRefundable = canRefund(startDate, request.status, request);
                const refundTimeRemaining = getRefundTimeRemaining(startDate);
                const isRefunding = refundingAvailabilityId === availabilityId;
                  
                return (
                  <div key={request._id} className={`p-3 border rounded-lg ${getStatusColor(request.status, request)}`}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-sm">
                        {formatLabel("therapistRequest")}
                      </span>
                      <span className="text-xs px-2 py-1 rounded-full bg-white bg-opacity-60">
                        {getStatusText(request.status, request)}
                      </span>
                    </div>
                    
                    <div className="text-sm space-y-1">
                      <div>
                        <strong>{formatLabel("therapist")}: </strong> 
                        {formatTherapistName(request.therapistId)}
                      </div>
                      
                      {formattedDate && (
                        <div>
                          <strong>{formatLabel("sessionDate")}: </strong> 
                          {formattedDateWithRange}
                        </div>
                      )}

                      {localizedMeetingName && (
                        <div>
                          <strong>{formatLabel("sessionName")}: </strong> 
                          {localizedMeetingName}
                        </div>
                      )}
                      
                      {/* Kalan süre göster */}
                      {startDate && (
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <Clock className="w-3 h-3" />
                          <span>{timeRemaining}</span>
                        </div>
                      )}
                      
                      {/* Refund durumu göster */}
                      {isRequestRefunded ? (
                        <div className="flex items-center gap-1 text-xs text-purple-600 mt-2 bg-purple-50 p-2 rounded">
                          <RefreshCw className="w-4 h-4" />
                          <span className="font-semibold">{t("appointmentRefunded")}</span>
                        </div>
                      ) : (
                        startDate && (request.status === 'accepted' || request.status === 'completed') && (
                          <div className="flex items-center gap-1 text-xs text-gray-600 mt-1">
                            <RefreshCw className="w-3 h-3" />
                            <span>{refundTimeRemaining}</span>
                          </div>
                        )
                      )}
                      
                      {/* Meeting kontrolü ve butonlar */}
                      {meetingUrl && request.status === 'accepted' && (
                        <div className="mt-2 space-y-2">
                          <div className="text-xs text-blue-600 break-all text-center sm:text-left">
                            <a
                              href={meetingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline font-medium"
                            >
                              {truncateLink(meetingUrl)}
                            </a>
                          </div>
                          {canJoin ? (
                            <div className="space-y-1">
                              {/* Direkt meeting URL'ini aç */}
                              <button 
                                onClick={() => handleJoinMeeting(meetingUrl, meetingId)}
                                className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-600 text-white hover:bg-green-700 w-full justify-center"
                              >
                                <Video className="w-3 h-3" />
                                {t("joinMeeting")}
                              </button>
                              
                              {/* Meeting URL'ini yeni sekmede aç */}
                              <button 
                                onClick={() => window.open(meetingUrl, '_blank')}
                                className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 w-full justify-center"
                              >
                                <ExternalLink className="w-3 h-3" />
                                {t("openInNewTab")}
                              </button>
                            </div>
                          ) : (
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {startDate && startDate > currentTime 
                                ? t("meetingWillBeAvailable5MinutesBefore") 
                                : t("meetingHasEnded")}
                            </div>
                          )}
                          
                          {/* Meeting URL'i var ama henüz zamanı gelmemiş - URL'i göster */}
                          {!canJoin && startDate && startDate > currentTime && (
                            <div className="mt-1">
                              <div className="text-xs text-gray-400 break-all">
                                URL hazır: {truncateLink(meetingUrl)}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Meeting URL yok ama meeting ID var */}
                      {!meetingUrl && meetingId && request.status === 'accepted' && (
                        <div className="mt-2">
                          <button 
                            onClick={() => handleStartMeeting(meetingId, null)}
                            disabled={isStarting || !canJoinMeeting(startDate)}
                            className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-full justify-center ${
                              isStarting || !canJoinMeeting(startDate)
                                ? 'bg-gray-400 text-white cursor-not-allowed' 
                                : 'bg-orange-600 text-white hover:bg-orange-700'
                            }`}
                          >
                            <Video className="w-3 h-3" />
                            {isStarting ? t("starting") : t("generateMeetingLink")}
                          </button>
                        </div>
                      )}
                      
                      {/* Refund Butonu - Sadece 24 saatten fazla kalan ve refund edilmemiş randevularda göster */}
                      {!isRequestRefunded && isRefundable && availabilityId && (
                        <div className="mt-2">
                          <button 
                            onClick={() => handleOpenRefundModal(request, availabilityId)}
                            disabled={isRefunding}
                            className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-full justify-center ${
                              isRefunding
                                ? 'bg-gray-400 text-white cursor-not-allowed' 
                                : 'bg-red-600 text-white hover:bg-red-700'
                            }`}
                          >
                            <RefreshCw className="w-3 h-3" />
                            {isRefunding ? t("refunding") : t("refundAppointment")}
                          </button>
                        </div>
                      )}
                      
                      {/* Reschedule Butonu - 48 saatten fazla kalan randevular için */}
                      {!isRequestRefunded && isRefundable && availabilityId && (
                        <div className="mt-2">
                          <button 
                            onClick={() => handleOpenRescheduleModal(request, availabilityId)}
                            className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-full justify-center bg-blue-600 text-white hover:bg-blue-700"
                          >
                            <CalendarPlus className="w-3 h-3" />
                            {t("rescheduleAppointment")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* İstatistikler */}
        <div className="mb-6 p-3 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">{t("requestSummary")}</h4>
          <div className="grid grid-cols-2 gap-2">
            <div className="p-2 bg-blue-50 rounded border border-blue-100">
              <div className="text-xl font-semibold text-blue-700">
                {futureRequests.length}
              </div>
              <div className="text-xs text-blue-600">{t("futureRequests")}</div>
            </div>
            <div className="p-2 bg-green-50 rounded border border-green-100">
              <div className="text-xl font-semibold text-green-700">
                {pastRequests.length}
              </div>
              <div className="text-xs text-green-600">{t("pastRequests")}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Refund Confirmation Modal */}
      {showRefundModal && selectedRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {t("confirmRefund")}
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                {t("refundConfirmationMessage")}
              </p>
              
              <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                <div>
                  <strong>{t("therapist")}: </strong>
                  {formatTherapistName(selectedRefund.request?.therapistId)}
                </div>
                <div>
                  <strong>{t("sessionDate")}: </strong>
                  {selectedRefund.request.formattedDate} {selectedRefund.request.formattedTimeRange}
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("refundReason")} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder={t("enterRefundReason")}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 min-h-[100px]"
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                {refundReason.length}/500 {t("characters")}
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleCloseRefundModal}
                disabled={refundLoading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
              >
                {t("cancel")}
              </button>
              <button
                onClick={handleConfirmRefund}
                disabled={refundLoading || !refundReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {refundLoading ? t("refunding") : t("confirmRefund")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Confirmation Modal */}
      {showRescheduleModal && selectedReschedule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {t("rescheduleAppointment")}
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                {t("rescheduleConfirmationMessage") || "Would you like to reschedule this appointment?"}
              </p>
              
              <div className="bg-blue-50 p-3 rounded-lg text-sm space-y-1">
                <div>
                  <strong>{t("therapist")}: </strong>
                  {formatTherapistName(selectedReschedule.request?.therapistId)}
                </div>
                <div>
                  <strong>{t("sessionDate")}: </strong>
                  {selectedReschedule.request.formattedDate} {selectedReschedule.request.formattedTimeRange}
                </div>
              </div>
              
              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-800">
                  <strong>📅 {t("note") || "Note"}:</strong> {t("rescheduleRedirectMessage") || "You will be redirected to the therapist's profile to select a new time slot."}
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleCloseRescheduleModal}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                {t("cancel")}
              </button>
              <button
                onClick={handleReschedule}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                {t("continue") || "Continue"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailPanel;
